import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

interface Prize {
  rank: number;
  name: string;
  description: string;
  value: number;
}

interface Campaign {
  id: string;
  lotteryId: string;
  name: string;
  description?: string;
  prizes?: Prize[];
  drawDate: string;
}

interface LotterySpinPageProps {}

const LotterySpinPage: React.FC<LotterySpinPageProps> = () => {
  const { tagId, campaignId } = useParams<{ tagId: string; campaignId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [spinning, setSpinning] = useState(false);
  const [campaignData, setCampaignData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // ⭐ KEY FIX: Prevent duplicate fetches
  const fetchedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const spinTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  
  // Get lottery result and campaign from navigation state (passed from ScanConsumerPage)
  const navigationState = location.state as { result?: any; lottery?: Campaign } | null;
  const joinResult = navigationState?.result;
  const lotteryInfo = navigationState?.lottery;
  
  console.log('🎰 LotterySpinPage mounted');
  console.log('📋 Params:', { tagId, campaignId });
  console.log('📦 Navigation state:', navigationState);
  
  // ✅ CRITICAL: Enhanced logging for entry ID
  if (joinResult) {
    console.log('🔍 Join Result structure:', {
      hasEntry: !!joinResult.entry,
      entry: joinResult.entry,
      entryId: joinResult.entry?._id,
      entryNumber: joinResult.entry?.entryNumber,
      timestamp: joinResult.entry?.timestamp
    });
  }
  
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cleanup on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    if (!tagId || !campaignId) {
      setError('Missing tag ID or campaign ID');
      setLoading(false);
      return;
    }
    
    // ⭐ CRITICAL FIX: Check if we already have data from navigation state
    if (joinResult && lotteryInfo) {
      console.log('✅ Using data from navigation state (no API call needed)');
      setCampaignData({ joinResult, lottery: lotteryInfo });
      setLoading(false);
      
      // Start spin animation immediately
      setTimeout(() => {
        startSpinAnimation();
      }, 500);
      return;
    }
    
    // ⭐ CRITICAL: Prevent duplicate API calls
    if (fetchedRef.current) {
      console.log('⚠️ Already fetched, skipping duplicate call');
      return;
    }
    
    // If we don't have navigation state, fetch from API
    const fetchCampaignData = async () => {
      fetchedRef.current = true;
      setLoading(true);
      setError(null);
      
      // Create abort controller for cleanup
      abortControllerRef.current = new AbortController();
      
      try {
        console.log('📡 Fetching campaign data for:', { tagId, campaignId });
        
        const response = await fetch(
          `/api/consumer-lottery/check-tag?tagId=${tagId}`,
          { 
            signal: abortControllerRef.current.signal,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('📥 Campaign data received:', data);
        
        if (!mountedRef.current) return;
        
        if (data.success) {
          setCampaignData(data);
          setLoading(false);
          
          // Start spin animation after data is loaded
          setTimeout(() => {
            startSpinAnimation();
          }, 500);
        } else {
          setError(data.message || 'Failed to load campaign data');
          setLoading(false);
          fetchedRef.current = false; // Allow retry
        }
      } catch (err: any) {
        // Ignore abort errors (happens on component unmount)
        if (err.name === 'AbortError') {
          console.log('🚫 Fetch aborted');
          return;
        }
        
        if (!mountedRef.current) return;
        
        console.error('❌ Error fetching campaign data:', err);
        setError('Failed to load campaign. Please try again.');
        setLoading(false);
        fetchedRef.current = false; // Allow retry
      }
    };
    
    fetchCampaignData();
  }, [tagId, campaignId, joinResult, lotteryInfo]); // Dependencies
  
  const startSpinAnimation = () => {
    console.log('🎰 Starting spin animation');
    setSpinning(true);
    
    // Spin for 3 seconds then navigate to result
    spinTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      
      setSpinning(false);
      console.log('✅ Spin complete, preparing navigation...');
      
      // ✅ CRITICAL FIX: Better entry ID extraction with detailed logging
    //const entryId = joinResult?.entry?._id;
      
// ✅ FINAL FIX: Robust entryId extraction with multiple fallbacks
const entryId =
  joinResult?.entry?._id ||           // backend _id (most reliable)
  joinResult?.entryId ||              // fallback if backend returned entryId instead
  joinResult?.entry?.entryNumber ||   // fallback if only entryNumber present
 joinResult?.entry?.entryId ||   // your backend uses this
  null;

      
      console.log('🔍 Entry ID extraction:', {
        hasJoinResult: !!joinResult,
        hasEntry: !!joinResult?.entry,
        entryObject: joinResult?.entry,
        extractedId: entryId,
        idType: typeof entryId,
        idLength: entryId?.length
      });

      setTimeout(() => {
        if (entryId) {
          console.log('✅ Using entry ID for navigation:', entryId);
          console.log('🚀 Navigating to:', `/consumer/lottery-result/${entryId}`);
          navigate(`/consumer/lottery-result/${entryId}`, {
            state: {
              result: joinResult,
              lottery: lotteryInfo,
               entryId: entryId
            }
          });


        } else {
          // ❌ CRITICAL: No fallback to tagId - show error instead
          console.error('❌ No entry ID found in join result');
          console.error('📋 Full join result:', JSON.stringify(joinResult, null, 2));
          
          setError('Failed to get lottery entry information. The entry may not have been created properly.');
          
          // Optionally, try to use entryNumber as fallback if it exists
          const entryNumber = joinResult?.entry?.entryNumber;
          if (entryNumber) {
            console.warn('⚠️ Attempting navigation with entryNumber:', entryNumber);
            navigate(`/consumer/lottery-result/${entryNumber}`, {
              state: {
                result: joinResult,
                lottery: lotteryInfo,
                useEntryNumber: true // Flag to indicate this is an entryNumber, not _id
              }
            });
          } else {
            console.error('❌ No entryNumber found either. Cannot navigate to result page.');
          }
        }
      }, 1000);

    }, 3000); // 3 second spin
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-400 via-pink-500 to-red-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-white mx-auto mb-6"></div>
          <p className="text-xl text-white font-semibold">Loading lottery...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-400 to-red-600 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="text-red-500 text-6xl mb-6">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button 
              onClick={() => {
                fetchedRef.current = false;
                setLoading(true);
                setError(null);
                window.location.reload();
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Retry
            </button>
            <button 
              onClick={() => navigate('/consumer')}
              className="w-full border border-gray-300 hover:bg-gray-50 py-3 px-6 rounded-lg font-medium transition-colors"
            >
              Back to Scanner
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Main spin animation
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 p-4 overflow-hidden">
      <div className="relative max-w-2xl w-full">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-yellow-400 rounded-full opacity-20 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-400 rounded-full opacity-20 blur-3xl"></div>
        </div>
        
        {/* Main content */}
        <div className="relative bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center">
          {/* Lottery name */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-2">
              {lotteryInfo?.name || 'Lottery Draw'}
            </h1>
            {lotteryInfo?.description && (
              <p className="text-gray-600">{lotteryInfo.description}</p>
            )}
          </div>
          
          {/* Spinning animation */}
          <div className="mb-8">
            <div className={`relative w-64 h-64 mx-auto ${spinning ? 'animate-spin-slow' : ''}`}>
              {/* Lottery wheel */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 shadow-2xl flex items-center justify-center">
                <div className="text-white text-7xl">🎰</div>
              </div>
              
              {/* Center circle */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-white shadow-xl flex items-center justify-center">
                  {spinning ? (
                    <div className="text-4xl animate-pulse">✨</div>
                  ) : (
                    <div className="text-4xl">🎁</div>
                  )}
                </div>
              </div>
              
              {/* Spinning indicators */}
              {spinning && (
                <>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-4 h-4 bg-yellow-300 rounded-full animate-ping"></div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-2 w-4 h-4 bg-pink-300 rounded-full animate-ping animation-delay-150"></div>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-4 h-4 bg-blue-300 rounded-full animate-ping animation-delay-300"></div>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-4 h-4 bg-green-300 rounded-full animate-ping animation-delay-450"></div>
                </>
              )}
            </div>
          </div>
          
          {/* Status text */}
          <div className="space-y-3">
            {spinning ? (
              <>
                <p className="text-2xl font-bold text-gray-800 animate-pulse">
                  Spinning...
                </p>
                <p className="text-gray-600">
                  🎲 Determining your prize...
                </p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-gray-800">
                  Entry Confirmed!
                </p>
                <p className="text-gray-600">
                  Preparing your results...
                </p>
              </>
            )}
          </div>
          
          {/* Prizes preview */}
          {lotteryInfo?.prizes && lotteryInfo.prizes.length > 0 && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-3">Available Prizes:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {lotteryInfo.prizes.slice(0, 3).map((prize: Prize, idx: number) => (
                  <div key={idx} className="px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full text-xs font-medium text-purple-700">
                    🎁 {prize.name}
                  </div>
                ))}
                {lotteryInfo.prizes.length > 3 && (
                  <div className="px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-600">
                    +{lotteryInfo.prizes.length - 3} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Confetti effect */}
        {!spinning && (
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `-${Math.random() * 20}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 2}s`,
                }}
              >
                {['🎉', '✨', '🎊', '⭐', '💫'][Math.floor(Math.random() * 5)]}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Custom animations */}
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-spin-slow {
          animation: spin-slow 2s linear infinite;
        }
        
        .animation-delay-150 {
          animation-delay: 0.15s;
        }
        
        .animation-delay-300 {
          animation-delay: 0.3s;
        }
        
        .animation-delay-450 {
          animation-delay: 0.45s;
        }
        
        @keyframes confetti {
          from {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          to {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        .animate-confetti {
          animation: confetti 3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default LotterySpinPage;